/**
 * A tiny synthesizer. Every sound (a jump blip, a music note, a snare hit) is described as
 * a list of VoiceSpecs, plain data that `scheduleVoices` turns into a few Web Audio nodes:
 * an oscillator + gain for a tone, a noise buffer + filter + gain for a noise burst. Keeping
 * the recipes as data makes them testable without Web Audio and keeps the node count per
 * sound small; finished nodes disconnect themselves.
 */

/** Oscillator shapes. The soft and pulse waves are band-limited (fewer harmonics: rounder, kinder to small speakers). */
export type Wave =
  | 'sine'
  | 'triangle'
  | 'square'
  | 'sawtooth'
  | 'pulse50'
  | 'pulse25'
  | 'pulse12'
  | 'softSquare'
  | 'softSaw'
  | 'organ';

/**
 * Attack, decay to a sustain level, hold, release. With `sustain: 0` the sound is
 * percussive: it fades out completely during `decay` (hold and release are ignored).
 */
export interface Envelope {
  attack: number;
  decay: number;
  /** 0..1 of the peak. */
  sustain: number;
  hold: number;
  release: number;
}

export interface ToneVoice {
  kind: 'tone';
  wave: Wave;
  /** Offset from the start of the sound, s. */
  at: number;
  /** Hz at the start. */
  freq: number;
  /** Glide target in Hz, reached `glide` seconds after the start (exponential). */
  freqTo?: number;
  glide?: number;
  /** Peak gain. */
  gain: number;
  env: Envelope;
}

export interface NoiseVoice {
  kind: 'noise';
  at: number;
  filter: BiquadFilterType;
  /** Filter frequency in Hz, optionally sweeping to `freqTo` over the whole voice. */
  freq: number;
  freqTo?: number;
  q?: number;
  gain: number;
  env: Envelope;
}

export type VoiceSpec = ToneVoice | NoiseVoice;

/** Counters for measurements and limits (nodes created, voices playing right now). */
export interface SynthStats {
  nodes: number;
  voices: number;
  active: number;
}

export function createSynthStats(): SynthStats {
  return { nodes: 0, voices: 0, active: 0 };
}

/**
 * The voices of one owner still sounding, with the time each one stops (a music player cuts
 * them short when it stops: a note ringing on unheard past the fade would only cost work).
 */
export type LiveVoices = Map<AudioScheduledSourceNode, number>;

/** Percussive envelope: quick attack, exponential fade over `decay` seconds. */
export function pluck(decay: number, attack = 0.004): Envelope {
  return { attack, decay, sustain: 0, hold: 0, release: 0 };
}

/** Sustained envelope for a note of `length` seconds (attack + decay + hold fill it). */
export function held(length: number, sustain: number, release: number, attack = 0.006): Envelope {
  const decay = Math.min(0.08, length / 3);
  return { attack, decay, sustain, hold: Math.max(0, length - attack - decay), release };
}

/** Seconds from the start of the voice until it is silent. */
export function envelopeLength(env: Envelope): number {
  return env.sustain > 0 ? env.attack + env.decay + env.hold + env.release : env.attack + env.decay;
}

/** Seconds from the start of the sound until its last voice is silent. */
export function soundLength(voices: readonly VoiceSpec[]): number {
  let end = 0;
  for (const voice of voices) end = Math.max(end, voice.at + envelopeLength(voice.env));
  return end;
}

/** Equal-tempered frequency of a MIDI note (69 = A4 = 440 Hz). */
export function midiToHz(midi: number): number {
  return 440 * 2 ** ((midi - 69) / 12);
}

/**
 * Schedules `voices` to start at `time` into `output`. `detune` (cents) shifts every tone,
 * e.g. for the music's tape effect. Returns the time the sound ends.
 */
export function scheduleVoices(
  ctx: BaseAudioContext,
  output: AudioNode,
  noise: AudioBuffer,
  voices: readonly VoiceSpec[],
  time: number,
  detune = 0,
  stats?: SynthStats,
  live?: LiveVoices,
): number {
  let end = time;
  for (const voice of voices) {
    const voiceEnd =
      voice.kind === 'tone'
        ? scheduleTone(ctx, output, voice, time + voice.at, detune, stats, live)
        : scheduleNoise(ctx, output, noise, voice, time + voice.at, stats, live);
    end = Math.max(end, voiceEnd);
  }
  return end;
}

function scheduleTone(
  ctx: BaseAudioContext,
  output: AudioNode,
  voice: ToneVoice,
  start: number,
  detune: number,
  stats: SynthStats | undefined,
  live: LiveVoices | undefined,
): number {
  const osc = ctx.createOscillator();
  setWave(ctx, osc, voice.wave);
  // A plain value unless the pitch glides: no automation timeline to evaluate.
  if (voice.freqTo !== undefined && voice.freqTo > 0) {
    osc.frequency.setValueAtTime(voice.freq, start);
    osc.frequency.exponentialRampToValueAtTime(voice.freqTo, start + (voice.glide ?? 0.1));
  } else {
    osc.frequency.value = voice.freq;
  }
  if (detune !== 0) osc.detune.value = detune;
  const gain = ctx.createGain();
  const end = applyEnvelope(gain.gain, start, voice.gain, voice.env);
  osc.connect(gain);
  gain.connect(output);
  osc.start(start);
  osc.stop(end + 0.02);
  track(osc, [osc, gain], stats, live, end + 0.02);
  return end;
}

/** Fresh offsets into the shared noise buffer, so consecutive hits do not sound identical. */
let noiseCursor = 0;

function scheduleNoise(
  ctx: BaseAudioContext,
  output: AudioNode,
  noise: AudioBuffer,
  voice: NoiseVoice,
  start: number,
  stats: SynthStats | undefined,
  live: LiveVoices | undefined,
): number {
  const source = ctx.createBufferSource();
  source.buffer = noise;
  source.loop = true;
  const filter = ctx.createBiquadFilter();
  filter.type = voice.filter;
  if (voice.q !== undefined) filter.Q.value = voice.q;
  const gain = ctx.createGain();
  const end = applyEnvelope(gain.gain, start, voice.gain, voice.env);
  // As for oscillators: automate the filter only when it sweeps.
  if (voice.freqTo !== undefined && voice.freqTo > 0) {
    filter.frequency.setValueAtTime(voice.freq, start);
    filter.frequency.exponentialRampToValueAtTime(voice.freqTo, Math.max(start + 0.005, end));
  } else {
    filter.frequency.value = voice.freq;
  }
  source.connect(filter);
  filter.connect(gain);
  gain.connect(output);
  noiseCursor = (noiseCursor + 0.137) % 0.9;
  source.start(start, noiseCursor);
  source.stop(end + 0.02);
  track(source, [source, filter, gain], stats, live, end + 0.02);
  return end;
}

/** Writes the envelope onto a gain param; returns the time it reaches silence. */
function applyEnvelope(param: AudioParam, start: number, peak: number, env: Envelope): number {
  const peakAt = start + Math.max(0.001, env.attack);
  param.setValueAtTime(0, start);
  param.linearRampToValueAtTime(peak, peakAt);
  if (env.sustain <= 0) {
    // Six time constants: the level is down by ~52 dB when the source stops.
    const end = peakAt + env.decay;
    param.setTargetAtTime(0, peakAt, Math.max(0.001, env.decay / 6));
    return end;
  }
  param.setTargetAtTime(peak * env.sustain, peakAt, Math.max(0.001, env.decay / 3));
  const releaseAt = peakAt + env.decay + env.hold;
  param.setTargetAtTime(0, releaseAt, Math.max(0.001, env.release / 6));
  return releaseAt + env.release;
}

/** Disconnects the nodes of a voice once its source has stopped. */
function track(
  source: AudioScheduledSourceNode,
  nodes: readonly AudioNode[],
  stats: SynthStats | undefined,
  live: LiveVoices | undefined,
  stopsAt: number,
): void {
  if (stats) {
    stats.nodes += nodes.length;
    stats.voices += 1;
    stats.active += 1;
  }
  live?.set(source, stopsAt);
  source.onended = () => {
    for (const node of nodes) node.disconnect();
    if (stats) stats.active -= 1;
    live?.delete(source);
  };
}

// ---- waveforms ----

/** Harmonics of the band-limited waves: enough for a chiptune edge, no harsh fizz. */
const HARMONICS = 24;
const SOFT_HARMONICS = 9;
/**
 * A drawbar organ: the fundamental, its octave, twelfth, second octave, the fifth above that
 * and the third octave (index = harmonic).
 */
const ORGAN_DRAWBARS = [0, 1, 0.55, 0.38, 0.3, 0, 0.14, 0, 0.1];

const periodicWaves = new WeakMap<BaseAudioContext, Map<Wave, PeriodicWave>>();

function setWave(ctx: BaseAudioContext, osc: OscillatorNode, wave: Wave): void {
  if (wave === 'sine' || wave === 'triangle' || wave === 'square' || wave === 'sawtooth') {
    osc.type = wave;
    return;
  }
  let cache = periodicWaves.get(ctx);
  if (!cache) {
    cache = new Map();
    periodicWaves.set(ctx, cache);
  }
  let periodic = cache.get(wave);
  if (!periodic) {
    const { real, imag } = waveCoefficients(wave);
    periodic = ctx.createPeriodicWave(real, imag);
    cache.set(wave, periodic);
  }
  osc.setPeriodicWave(periodic);
}

/** Fourier series of the custom waves (index = harmonic; the DC term stays 0). */
export function waveCoefficients(
  wave: Exclude<Wave, 'sine' | 'triangle' | 'square' | 'sawtooth'>,
): {
  real: Float32Array<ArrayBuffer>;
  imag: Float32Array<ArrayBuffer>;
} {
  const count =
    wave === 'organ'
      ? ORGAN_DRAWBARS.length - 1
      : wave === 'softSquare' || wave === 'softSaw'
        ? SOFT_HARMONICS
        : HARMONICS;
  const real = new Float32Array(count + 1);
  const imag = new Float32Array(count + 1);
  for (let n = 1; n <= count; n++) {
    switch (wave) {
      case 'pulse50':
      case 'pulse25':
      case 'pulse12': {
        // A pulse of duty d: cosine terms 2/(nπ)·sin(nπd).
        const duty = wave === 'pulse50' ? 0.5 : wave === 'pulse25' ? 0.25 : 0.125;
        real[n] = (2 / (n * Math.PI)) * Math.sin(n * Math.PI * duty);
        break;
      }
      case 'softSquare':
        imag[n] = n % 2 === 1 ? 4 / (n * Math.PI) : 0;
        break;
      case 'softSaw':
        imag[n] = ((n % 2 === 1 ? 1 : -1) * 2) / (n * Math.PI);
        break;
      case 'organ':
        imag[n] = ORGAN_DRAWBARS[n] ?? 0;
        break;
    }
  }
  return { real, imag };
}

/** One second of white noise, the same every time (offline renders are reproducible). */
export function createNoiseBuffer(ctx: BaseAudioContext, seconds = 1): AudioBuffer {
  const length = Math.max(1, Math.round(ctx.sampleRate * seconds));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let state = 0x9e3779b9;
  for (let i = 0; i < length; i++) {
    // xorshift32
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    data[i] = ((state >>> 0) / 4294967296) * 2 - 1;
  }
  return buffer;
}
